// ProjectForm.tsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useFormContext, Controller } from "react-hook-form";
import Select, { StylesConfig } from "react-select";

import CharacterCounter from "@services/CharacterCounter";
import EmojiPicker from "emoji-picker-react";
import DateInput from "@shared/components/DateInput";
import MapBox from "@shared/components/Mapbox/Mapbox";
import { MapProvider } from "@shared/components/Mapbox/MapContext";
import {
  CurrencyOption,
  ProjectFormProps,
  Currency,
} from "@shared/interfaces/ProjectsFiles";
import { handleCurrencyChange } from "../utils/utils";
import CountriesMultiSelect from "@shared/components/CountriesMultiSelect";
import useFetchCountries from "../utils/useFetchCountries";
import { currencyService } from "@services/CurrencyService";

const selectStyles: StylesConfig<CurrencyOption, false> = {
  control: (base) => ({
    ...base,
    backgroundColor: "white",
    minHeight: 42,
    borderColor: "#D1D5DB",
    boxShadow: "none",
    fontSize: "0.875rem",
    ":disabled": {
      color: "gray",
      cursor: "not-allowed",
    },
    ":hover": {
      borderColor: "#9CA3AF",
    },
  }),
  menu: (base) => ({ ...base, zIndex: 20 }),
};

const ProjectForm: React.FC<ProjectFormProps> = (props) => {
  const {
    register,
    setValue,
    watch,
    setError,
    clearErrors,
    getValues,
    formState: { errors },
    control,
  } = useFormContext();

  const [coordinates, setCoordinates] = useState<[number, number] | null>(null);
  const [countryBorders, setCountryBorders] = useState<any | null>(null);
  const { countries } = useFetchCountries();
  const bordersCache = useRef(new Map<string, GeoJSON.FeatureCollection>());
  const [firstCountryName, setFirstCountryName] = useState<string>("");

  const [currencies, setCurrencies] = useState<Currency[]>([]);

  // form values
  const values = watch();
  const countryIds: number[] = watch("countryIds") || [];
  const currencyId: number | null = watch("currencyId") ?? null;

  const selectedCurrency = useMemo(
    () => currencies.find((c) => c.id === currencyId) || null,
    [currencies, currencyId]
  );

  const currencyOptions = useMemo<CurrencyOption[]>(
    () =>
      currencies.map((c) => ({
        value: c.id,
        label: `${c.code}`,
        code: c.code,
        symbol: c.symbol,
      })),
    [currencies]
  );

  const maxNameLength = 60;
  const maxDescriptionLength = 500;

  // Country → coordinates & borders helpers
  const fetchCoordinates = async (countryName: string) => {
    try {
      const response = await fetch(
        `https://restcountries.com/v3.1/name/${countryName}`
      );
      const data = await response.json();

      if (Array.isArray(data)) {
        const matchedCountry =
          data.find(
            (country: any) =>
              country.name.common.toLowerCase() === countryName.toLowerCase() ||
              country.name.official.toLowerCase() === countryName.toLowerCase()
          ) || data[0];

        const capitalCoordinates = matchedCountry?.capitalInfo?.latlng;
        const countryCoordinates = matchedCountry?.latlng;
        const newCoordinates = capitalCoordinates || countryCoordinates;

        if (newCoordinates) {
          setCoordinates([newCoordinates[1], newCoordinates[0]]);
        }

        fetchCountryBorders(matchedCountry?.cca3);
      }
    } catch (error) {
      console.error("Error fetching country coordinates:", error);
    }
  };

  const fetchCountryBorders = async (countryCode: string) => {
    try {
      const response = await fetch(
        `https://raw.githubusercontent.com/johan/world.geo.json/master/countries/${countryCode}.geo.json`
      );
      const data = await response.json();
      setCountryBorders(data);
    } catch (error) {
      console.error("Error fetching country borders:", error);
    }
  };

  // Effects
  useEffect(() => {
    register("countryIds", {
      validate: (v) =>
        Array.isArray(v) && v.length > 0 && v.length <= 5
          ? true
          : "Please select 1–5 countries",
    });

    //  fetch currencies
    const fetchCurrencies = async () => {
      try {
        const response = await currencyService.getCurrencies();
        const items: Currency[] = Array.isArray(response.currencies)
          ? response.currencies
          : response?.currencies ?? [];
        setCurrencies(items);

        // Set default CHF if currencyId not set
        const current = getValues("currencyId");
        if (!current && items.length) {
          const chf = items.find((c) => c.code === "CHF") ?? items[0];
          setValue("currencyId", chf.id, { shouldValidate: true });
        }
      } catch (e) {
        console.error("Failed to fetch currencies", e);
      }
    };

    fetchCurrencies();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchFC = async (
      code: string
    ): Promise<GeoJSON.FeatureCollection | null> => {
      try {
        const cached = bordersCache.current.get(code);
        if (cached) return cached;

        const url = `https://raw.githubusercontent.com/johan/world.geo.json/master/countries/${code}.geo.json`;
        const res = await fetch(url);

        if (!res.ok) return null;

        const data = await res.json();
        const fc =
          data?.type === "FeatureCollection"
            ? data
            : data?.type === "Feature"
            ? { type: "FeatureCollection", features: [data] }
            : null;

        if (fc) bordersCache.current.set(code, fc);
        return fc;
      } catch {
        return null;
      }
    };

    const run = async () => {
      if (!countries || countryIds.length === 0) {
        setCountryBorders(null);
        return;
      }

      const selected = countries.filter((c: any) => countryIds.includes(c.id));
      const codes = selected
        .map((c: any) => c.code)
        .filter(Boolean) as string[];

      if (codes.length === 0) {
        setCountryBorders(null);
        return;
      }

      const results = await Promise.allSettled(codes.map(fetchFC));
      if (cancelled) return;

      const featureCollections = results
        .filter(
          (r): r is PromiseFulfilledResult<GeoJSON.FeatureCollection | null> =>
            r.status === "fulfilled"
        )
        .map((r) => r.value)
        .filter(
          (fc): fc is GeoJSON.FeatureCollection =>
            !!fc && Array.isArray(fc.features) && fc.features.length > 0
        );

      if (featureCollections.length > 0) {
        const merged: GeoJSON.FeatureCollection = {
          type: "FeatureCollection",
          features: featureCollections.flatMap((fc) => fc.features),
        };
        setCountryBorders(merged);
      } else {
        setCountryBorders(null);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [countryIds, countries]);

  const country = getValues("countryName");

  useEffect(() => {
    if (firstCountryName) fetchCoordinates(firstCountryName);
  }, [firstCountryName]);

  useEffect(() => {
    if (country) fetchCoordinates(country);
  }, [country]);

  // UI
  return (
    <div className="flex w-full flex-grow bg-white space-x-12 px-1">
      <div className="w-full flex flex-col space-y-4">
        <div className="relative">
          <label className="block  font-semibold text-sm 2xl:text-base  text-gray-700 mb-2">
            Project name <span className="text-red-600">*</span>
          </label>
          <div className="flex items-center mb-2 h-10">
            <input
              type="text"
              {...register("name", { required: "Project Name is required" })}
              className={`w-full text-sm 2xl:text-base p-2 border rounded mr-3 h-full ${
                props.isEditing
                  ? "border-gray-300 focus:outline-none focus:border-blue-500"
                  : "border-gray-300 text-slate-400"
              }`}
              placeholder="Add a name"
              maxLength={maxNameLength}
              readOnly={!props.isEditing}
            />
            <button
              type="button"
              className="border border-gray-300 rounded px-3 h-full flex items-center justify-center hover:bg-gray-200"
              onClick={() => props.setShowEmojiPicker(!props.showEmojiPicker)}
              disabled={!props.isEditing}
            >
              {props.icon || (
                <img
                  src="/add_reaction.svg"
                  className="w-5 h-5"
                  alt="Add reaction"
                />
              )}
            </button>
          </div>
          {props.showEmojiPicker && (
            <div className="absolute mt-2 right-0 z-[10]">
              <EmojiPicker
                onEmojiClick={(emoji) => {
                  props.handleEmojiSelect(emoji);
                  props.setShowEmojiPicker(false);
                }}
              />
            </div>
          )}
          <CharacterCounter
            currentLength={values.name?.length || 0}
            maxLength={maxNameLength}
          />
          {errors.name?.message && (
            <p className="text-red-500 text-sm">
              {String(errors.name.message)}
            </p>
          )}
        </div>

        <div>
          <label className="block font-semibold text-sm 2xl:text-base  text-gray-700 mb-2">
            Project description <span className="text-red-600">*</span>
          </label>
          <textarea
            {...register("description", {
              required: "Project Description is required",
            })}
            className={`w-full p-2 text-sm 2xl:text-base border rounded ${
              props.isEditing
                ? "border-gray-300 focus:outline-none focus:border-blue-500"
                : "border-gray-300 text-slate-400"
            }`}
            placeholder="Type anything..."
            rows={7}
            maxLength={maxDescriptionLength}
            readOnly={!props.isEditing}
          />
          <CharacterCounter
            currentLength={values.description?.length || 0}
            maxLength={maxDescriptionLength}
          />
          {errors.description?.message && (
            <p className="text-red-500 text-sm">
              {String(errors.description.message)}
            </p>
          )}
        </div>

        <div>
          <label className="block font-semibold text-gray-700 mb-2 text-sm 2xl:text-base">
            Location<span className="text-red-600">*</span>
          </label>
          <CountriesMultiSelect isEditing={props.isEditing} />
          {errors.countryIds?.message && (
            <p className="text-red-500 text-sm mt-1">
              {String(errors.countryIds.message)}
            </p>
          )}
        </div>

        <div className="flex space-x-4">
          <div className="flex-1">
            <label className="block font-semibold text-gray-700 mb-2 text-sm 2xl:text-base">
              Start Date <span className="text-red-600">*</span>
            </label>
            <DateInput
              {...register("startDate", {
                required: "Start Date is required",
                validate: (value) =>
                  !values.endDate || new Date(value) <= new Date(values.endDate)
                    ? true
                    : "Start Date should be less than or equal to End Date",
              })}
              value={values.startDate}
              isEditing={props.isEditing}
            />
            {errors.startDate?.message && (
              <p className="text-red-500 text-sm">
                {String(errors.startDate.message)}
              </p>
            )}
          </div>
          <div className="flex-1">
            <label className="block font-semibold text-gray-700 mb-2 text-sm 2xl:text-base">
              End Date <span className="text-red-600">*</span>
            </label>
            <DateInput
              {...register("endDate", {
                required: "End Date is required",
                validate: (value) =>
                  !values.startDate ||
                  new Date(value) >= new Date(values.startDate)
                    ? true
                    : "End Date should be greater than or equal to Start Date",
              })}
              value={values.endDate}
              isEditing={props.isEditing}
            />
            {errors.endDate?.message && (
              <p className="text-red-500 text-sm">
                {String(errors.endDate.message)}
              </p>
            )}
          </div>
        </div>

        {/*  NEW: Currency + Budget row */}
        <div>
          <label className="block font-semibold text-gray-700 mb-2 text-sm 2xl:text-base">
            Budget ({selectedCurrency?.symbol}){" "}
            <span className="text-red-600">*</span>
          </label>

          <div className="flex gap-3">
            {/* Currency select */}

            {/* Budget input with dynamic symbol hint */}
            <div className="flex-1 relative flex space-x-1 mb-12">
              <div className="w-32">
                <Controller
                  control={control}
                  name="currencyId"
                  rules={{ required: "Currency is required" }}
                  render={({ field }) => (
                    <Select
                      inputId="currencyId"
                      instanceId="currencyId"
                      isDisabled={!props.isEditing}
                      options={currencyOptions}
                      value={
                        currencyOptions.find((o) => o.value === field.value) ??
                        null
                      }
                      onChange={(opt) =>
                        field.onChange(
                          (opt as CurrencyOption | null)?.value ?? null
                        )
                      }
                      placeholder=""
                      classNamePrefix="react-select"
                      styles={selectStyles}
                    />
                  )}
                />
                {errors.currencyId?.message && (
                  <p className="text-red-500 text-sm mt-1">
                    {String(errors.currencyId.message)}
                  </p>
                )}
              </div>
              <input
                type="text"
                {...register("formattedBudget", {
                  required: "Budget is required",
                  pattern: {
                    value: /^[0-9]{1,3}(?:,[0-9]{3})*(?:\.\d{1,2})?$/,
                    message:
                      "Invalid budget format (e.g., 1,000.50 or 1000.50)",
                  },
                })}
                className={`w-full text-sm 2xl:text-base p-2 border rounded ${
                  props.isEditing
                    ? "border-gray-300 focus:outline-none focus:border-blue-500"
                    : "border-gray-300 text-slate-400"
                } `}
                placeholder={`Example: 100,000.00 (set 0 if not applicable)`}
                readOnly={!props.isEditing}
                onChange={(e) =>
                  handleCurrencyChange(
                    e,
                    "formattedBudget",
                    setValue,
                    clearErrors,
                    setError
                  )
                }
              />
              {errors.formattedBudget?.message && (
                <p className="text-red-500 text-sm">
                  {String(errors.formattedBudget.message)}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="w-full flex flex-col">
        {(props.createForm || props.isEditing) && (
          <label className="block text-sm 2xl:text-base text-gray-400 mb-2">
            Draw an area on the map
          </label>
        )}
        <div className="relative">
          <div className="h-80 2xl:h-96 w-full mt-2">
            <MapProvider>
              <MapBox
                isEditing={props.isEditing}
                onGeoJsonUpdate={props.handleGeoJsonUpdate}
                originalGeoJson={props.originalGeoJson}
                coordinates={coordinates}
                countryBorders={countryBorders}
              />
            </MapProvider>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectForm;
